'use client';
import type { Option } from '@susu/custom-made/utils/interfaces';
import { onEnter } from '@susu/custom-made/utils/utils';
import Button, { ButtonSize, ButtonType } from '../button/button';
import cn from 'classnames';
import { PREVIEW_HEADER_TEST_IDS, SECTION_CONTAINER_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { useI18N } from '@susu/i18n/client';
import { Level } from '@susu/custom-made/utils/constants';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import { useNavigation } from './useNavigation';

/** Scrollbar hidden, not scrolling: the tab row must stay reachable by wheel, touch and keyboard. */
const SCROLLER_CLASSES =
  'flex flex-1 justify-center items-center text-center py-6 px-16 md:px-120 lg:pt-20 lg:px-0 lg:pb-[13px] lg:overflow-x-auto lg:min-w-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden 2xl:pt-20 2xl:pb-[17px]';

/**
 * `lg:whitespace-nowrap` only holds because each item carries `lg:shrink-0` below — as flex
 * children they would otherwise be squeezed under their content width, which is what forces a wrap.
 */
const TAB_CLASSES =
  'py-10 px-16 rounded-full text-xs not-italic font-light leading-xs cursor-pointer lg:px-20 lg:text-sm lg:leading-sm lg:whitespace-nowrap';

/** `lg:h-40` spans the pill, not the label, so the mask also covers the active tab's background. */
const NAV_OVERFLOW_CLASSES = 'lg:top-20 lg:h-40 lg:w-[47px]';

/** Opaque white before the last 20px, where the chevron button sits. */
const NAV_FADE_PREV =
  'bg-[linear-gradient(to_left,rgba(255,255,255,0)_0%,rgb(255,255,255)_50%,rgb(255,255,255)_100%)]';
const NAV_FADE_NEXT =
  'bg-[linear-gradient(to_right,rgba(255,255,255,0)_0%,rgb(255,255,255)_50%,rgb(255,255,255)_100%)]';

export type Item =
  | {
      text: string;
      id: string;
      option?: Option;
      isActive: boolean;
      hasMissingValues: boolean;
    }
  | undefined;

export default function Navigation({
  items,
  testId,
  onItemClick,
  onFinishClick = () => {},
  showFinishButton = false,
}: Readonly<{
  items: Item[];
  testId: string;
  onItemClick: (item: Item | Option) => void;
  onFinishClick?: () => void;
  showFinishButton?: boolean;
}>) {
  const { t } = useI18N();
  const { level, isFinishButtonDisabled } = useNavigationContext();
  const { itemsContainerRef, canScrollToPrev, canScrollToNext, scrollItems } = useNavigation(items);
  const hasOverflow = canScrollToPrev || canScrollToNext;
  const isFinishButtonVisible = showFinishButton && level === Level.Group;

  return (
    <div className="flex justify-between items-center lg:px-20 lg:gap-20">
      <div className="relative flex items-center min-w-0 flex-1">
        {canScrollToPrev && (
          <div
            className={cn(
              'hidden lg:flex absolute left-0 items-center justify-start z-1 pointer-events-none',
              NAV_OVERFLOW_CLASSES,
              NAV_FADE_PREV,
            )}
          >
            <div className="pointer-events-auto">
              <Button
                onClick={() => scrollItems('prev')}
                testId={SECTION_CONTAINER_TEST_IDS.SECTION_NAV_SCROLL_PREV}
                type={ButtonType.IconOnlyRound}
                size={ButtonSize.Small}
                noBorder={true}
              >
                <i className="susu-icons" translate="no">
                  arrow_left
                </i>
              </Button>
            </div>
          </div>
        )}
        <div
          className={cn(SCROLLER_CLASSES, hasOverflow || isFinishButtonVisible ? 'lg:justify-start' : 'lg:justify-center')}
          data-testid={testId}
          ref={itemsContainerRef}
        >
          {items.map((item, index) => {
            if (item) {
              return (
                <div
                  className="flex cursor-pointer py-4 lg:p-0 lg:shrink-0"
                  key={item.id}
                  onClick={() => {
                    onItemClick(item.option ?? item);
                  }}
                  role="button"
                  tabIndex={index + 1}
                  onKeyDown={e => {
                    onEnter(e, () => {
                      onItemClick(item.option ?? item);
                    });
                  }}
                >
                  <span
                    className={cn(
                      TAB_CLASSES,
                      {
                        'bg-gray-300': item.isActive,
                        'text-red-600': item.hasMissingValues,
                        'md:text-gray-800': !item.hasMissingValues,
                      },
                    )}
                    data-test-section={`${item.text}-Section`}
                    data-testid={`${item.id}`}
                    aria-selected={item.isActive ? 'true' : 'false'}
                  >
                    {item.text}
                  </span>
                </div>
              );
            }
          })}
        </div>
        {canScrollToNext && (
          <div
            className={cn(
              'hidden lg:flex absolute right-0 items-center justify-end z-1 pointer-events-none',
              NAV_OVERFLOW_CLASSES,
              NAV_FADE_NEXT,
            )}
          >
            <div className="pointer-events-auto">
              <Button
                onClick={() => scrollItems('next')}
                testId={SECTION_CONTAINER_TEST_IDS.SECTION_NAV_SCROLL_NEXT}
                type={ButtonType.IconOnlyRound}
                size={ButtonSize.Small}
                noBorder={true}
              >
                <i className="susu-icons" translate="no">
                  arrow_right
                </i>
              </Button>
            </div>
          </div>
        )}
      </div>
      {isFinishButtonVisible && (
        <div className="hidden lg:block lg:self-start lg:mt-20">
          <Button
            onClick={onFinishClick}
            testId={PREVIEW_HEADER_TEST_IDS.SIDE_PANEL_SAVE_BTN}
            data-dd-action-name="Finish"
            isDisabled={isFinishButtonDisabled}
            type={ButtonType.Primary}
            size={ButtonSize.Large}
            noBorder={true}
          >
            {t('previewHeader.finish')}
          </Button>
        </div>
      )}
    </div>
  );
}
